import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Icon,
  Input,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui';
import { SignInName } from '@/features/auth/SignInName';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage, fieldErrors } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/** What closing does, in the order the server does it. */
const CONSEQUENCES = [
  'Your live and pending internships are taken off the site.',
  'Every open application is closed, with any interview cancelled and offer withdrawn. Each candidate is told.',
  'A paid plan stops renewing at the end of the period you have paid for.',
  'You and everyone on your team are signed out and can no longer sign in.',
];

/**
 * Close the company account. Owner only -- the page shows it to nobody else,
 * and the API refuses anyone else regardless.
 *
 * Two confirmations, as the API asks: the owner's password, and the company's
 * name typed out. Nothing is erased; support can restore the account.
 */
export function CloseCompanyCard({ companyName }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const close = useMutation({
    mutationFn: () =>
      companyApi.closeCompany({
        password,
        company_name: typedName,
        reason: reason.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(`${companyName} has been closed. We have emailed you a confirmation.`);
      await signOut();
      navigate(paths.home, { replace: true });
    },
  });

  const serverErrors = close.isError ? fieldErrors(close.error) : {};
  const nameMatches = typedName.trim().toLowerCase() === companyName.trim().toLowerCase();

  function dismiss() {
    if (close.isPending) return;
    setOpen(false);
    setPassword('');
    close.reset();
  }

  return (
    <>
      <Card className="border-danger-200">
        <CardHeader
          title="Close company account"
          description={`Closes ${companyName} for everyone on your team. Only the owner can do this.`}
        />
        <ul className="text-ink-muted mb-4 list-disc space-y-1 pl-5 text-sm">
          {CONSEQUENCES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button variant="danger" onClick={() => setOpen(true)}>
            <Icon name="trash" size={16} />
            Close company account
          </Button>
        </div>
      </Card>

      <Modal
        open={open}
        title={`Close ${companyName}?`}
        description="Nothing is erased. If you change your mind, contact support and the account can be restored -- but candidates will already have been told."
        onClose={dismiss}
      >
        <div className="space-y-4">
          <Alert tone="warning">You will be signed out straight away, and so will your team.</Alert>
          <SignInName />
          <Input
            label={`Type "${companyName}" to confirm`}
            value={typedName}
            autoComplete="off"
            onChange={(event) => setTypedName(event.target.value)}
            error={serverErrors.company_name}
          />
          <Input
            label="Your password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={serverErrors.password}
          />
          <Textarea
            label="Why are you leaving? (optional)"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {close.isError && Object.keys(serverErrors).length === 0 && (
            <Alert tone="danger">{errorMessage(close.error, 'Could not close the account')}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={dismiss} disabled={close.isPending}>
              Keep the account
            </Button>
            <Button
              variant="danger"
              disabled={!nameMatches || !password}
              isLoading={close.isPending}
              onClick={() => close.mutate()}
            >
              Close company account
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
